/**
 * Interview Plan Agent — la subtab (spec de TA, 30/09/2026).
 *
 * No es un chat: se elige una Job Description de las que ya están en la
 * plataforma, se aprieta Generar y a los 1 a 3 minutos hay una guía de
 * entrevista para bajar en Word.
 *
 * La generación va por la Function URL de los agentes de TA y no por tRPC:
 * CloudFront corta a los 30 segundos y esto tarda minutos.
 *
 * Visibilidad (TA, 07/10/2026): TA (`canManage`) ve todas, genera y asigna
 * Hiring Manager y Bar Raiser; cada uno de ellos ve la guía COMPLETA, solo de su
 * búsqueda (el backend filtra: acá no se esconde nada que haya llegado).
 */
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, FileDown, FileText, Languages, Sparkles, Trash2, UserPlus, X } from 'lucide-react';
import { toast } from 'sonner';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { WinclapLoader } from '@/components/ui/winclap-loader';
import { usePagination } from '@/hooks/usePagination';
import { trpc } from '@/lib/trpc';
import { toastError } from '@/lib/toast-error';
import { formatDateTime } from '@/lib/utils';
import { foldKey } from '@/../../shared/text-normalize';
import type { RouterOutputs } from '@/lib/api-types';
import { interviewPlanDirect } from '@/lib/ta-api';
import { MemberPickerDialog } from '@/components/governance/MemberPickerDialog';
import { INTERVIEW_GUIDE_SCHEMA_VERSION } from '@/../../shared/types/ta-interview';

const k = 'talent_acquisition.interview';

type JdDisponible = RouterOutputs['taMetrics']['interview']['sources'][number];
type Plan = RouterOutputs['taMetrics']['interview']['list'][number];
type Persona = RouterOutputs['taMetrics']['interview']['people'][number];

/** Las personas para asignar y mostrar por nombre. Solo TA las pide (canManage). */
function usePersonas(enabled: boolean) {
  const q = trpc.taMetrics.interview.people.useQuery(undefined, { enabled, staleTime: 5 * 60_000 });
  const porEmail = useMemo(() => new Map((q.data ?? []).map((p) => [p.email.toLowerCase(), p])), [q.data]);
  return { ...q, nombre: (email: string | null) => (email ? porEmail.get(email)?.name ?? email : null) };
}

/** Hiring Manager y Bar Raiser de una guía. TA los cambia; el resto los ve. */
function Entrevistadores({ plan, canManage }: { plan: Pick<Plan, 'planId' | 'hmEmail' | 'brEmail'>; canManage: boolean }) {
  const { t } = useTranslation();
  const utils = trpc.useUtils();
  const personas = usePersonas(canManage);
  const [eligiendo, setEligiendo] = useState<'hm' | 'br' | null>(null);
  const guardar = trpc.taMetrics.interview.setInterviewers.useMutation({
    onSuccess: () => {
      toast.success(t(`${k}.interviewers_saved`));
      utils.taMetrics.interview.list.invalidate();
      utils.taMetrics.interview.get.invalidate({ planId: plan.planId });
    },
    onError: (e) => toastError(e, t(`${k}.interviewers_failed`)),
  });
  const asignar = (rol: 'hm' | 'br', email: string | null) => guardar.mutate({
    planId: plan.planId,
    hmEmail: rol === 'hm' ? email : plan.hmEmail,
    brEmail: rol === 'br' ? email : plan.brEmail,
  });

  return (
    <div className="grid gap-2 sm:grid-cols-2">
      {(['hm', 'br'] as const).map((rol) => {
        const email = rol === 'hm' ? plan.hmEmail : plan.brEmail;
        return (
          <div key={rol} className="flex items-center justify-between gap-2 rounded-lg border p-2">
            <div className="min-w-0">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{t(`${k}.stage_${rol}`)}</div>
              <div className="truncate text-sm">{personas.nombre(email) ?? <span className="text-muted-foreground">{t(`${k}.unassigned`)}</span>}</div>
            </div>
            {canManage && (
              <div className="flex shrink-0 gap-1">
                <Button size="sm" variant="ghost" className="h-7 px-2" disabled={guardar.isPending} onClick={() => setEligiendo(rol)} aria-label={t(`${k}.assign`)}>
                  <UserPlus className="h-3.5 w-3.5" />
                </Button>
                {email && (
                  <Button size="sm" variant="ghost" className="h-7 px-2" disabled={guardar.isPending} onClick={() => asignar(rol, null)} aria-label={t(`${k}.unassign`)}>
                    <X className="h-3.5 w-3.5" />
                  </Button>
                )}
              </div>
            )}
          </div>
        );
      })}
      {canManage && (
        <MemberPickerDialog<Persona>
          open={!!eligiendo}
          onOpenChange={(o) => { if (!o) setEligiendo(null); }}
          title={eligiendo ? t(`${k}.pick_${eligiendo}`) : ''}
          description={t(`${k}.pick_desc`)}
          searchPlaceholder={t(`${k}.search_person`)}
          noResultsText={t(`${k}.no_person`)}
          members={personas.data ?? []}
          loading={personas.isLoading}
          exclude={new Set([plan.hmEmail, plan.brEmail].filter((x): x is string => !!x))}
          onPick={(m) => { if (eligiendo) asignar(eligiendo, m.email.toLowerCase()); setEligiendo(null); }}
        />
      )}
    </div>
  );
}

/** Elegir la JD y generar. El botón queda bloqueado mientras corre. */
function GenerarDialog({ open, onClose, onDone, onSeguir }: { open: boolean; onClose: () => void; onDone: () => void; onSeguir: (planId: string) => void }) {
  const { t } = useTranslation();
  const [busqueda, setBusqueda] = useState('');
  const [generando, setGenerando] = useState<string | null>(null);
  const sources = trpc.taMetrics.interview.sources.useQuery(undefined, { enabled: open, staleTime: 30_000 });

  const filtradas = useMemo(() => {
    const q = foldKey(busqueda);
    return (sources.data ?? []).filter((s) => !q || foldKey(s.titulo).includes(q) || foldKey(s.detalle).includes(q));
  }, [sources.data, busqueda]);

  const generar = async (jd: JdDisponible) => {
    setGenerando(jd.titulo);
    try {
      const { planId } = await interviewPlanDirect(jd.ref);
      onDone();          // la fila aparece como "generando" enseguida
      onSeguir(planId);  // y la pantalla la sigue hasta que esté
      onClose();
    } catch (e) {
      toastError(e, t(`${k}.generate_failed`));
    } finally {
      setGenerando(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !generando) onClose(); }}>
      <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t(`${k}.pick_jd`)}</DialogTitle>
          <p className="text-xs text-muted-foreground">{t(`${k}.pick_jd_hint`)}</p>
        </DialogHeader>
        {generando ? (
          <div className="flex flex-col items-center gap-3 py-14 text-center">
            <WinclapLoader size={40} />
            <p className="text-sm font-medium">{t(`${k}.generating`, { position: generando })}</p>
            <p className="max-w-sm text-xs text-muted-foreground">{t(`${k}.generating_hint`)}</p>
          </div>
        ) : (
          <div className="space-y-3">
            <Input
              id="ta-interview-buscar"
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              placeholder={t(`${k}.search_jd`)}
              className="h-9"
            />
            {sources.isLoading && <div className="flex justify-center py-10"><WinclapLoader size={28} /></div>}
            {sources.error && (
              <Alert variant="destructive">
                <AlertTitle>{t(`${k}.sources_error`)}</AlertTitle>
                <AlertDescription>{sources.error.message}</AlertDescription>
              </Alert>
            )}
            {sources.data && filtradas.length === 0 && (
              <p className="py-10 text-center text-sm text-muted-foreground">{t(`${k}.no_sources`)}</p>
            )}
            {filtradas.map((s) => (
              <button
                key={`${s.ref.source}-${s.titulo}-${s.fecha}`}
                type="button"
                onClick={() => generar(s)}
                className="flex w-full items-center justify-between gap-3 rounded-lg border p-3 text-left transition-colors hover:bg-muted/60"
              >
                <span className="min-w-0">
                  <span className="block truncate text-sm font-medium">{s.titulo}</span>
                  <span className="block truncate text-xs text-muted-foreground">
                    {t(`${k}.source_${s.ref.source}`)}{s.detalle ? ` · ${s.detalle}` : ''}{s.fecha ? ` · ${formatDateTime(s.fecha)}` : ''}
                  </span>
                </span>
                <Sparkles className="h-4 w-4 shrink-0 text-brand-green" />
              </button>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/** La guía ya generada: avisos, resumen por instancia y el botón de bajarla. */
function GuiaDialog({ planId, onClose, canManage }: { planId: string | null; onClose: () => void; canManage: boolean }) {
  const { t } = useTranslation();
  const plan = trpc.taMetrics.interview.get.useQuery({ planId: planId ?? '' }, { enabled: !!planId });
  const exportar = trpc.taMetrics.interview.exportDocx.useMutation({
    onSuccess: (r) => window.open(r.url, '_blank', 'noopener'),
    onError: (e) => toastError(e, t(`${k}.export_failed`)),
  });
  const g = plan.data?.guide;
  // Las guías del 30/09 son del schema provisorio: se ven, pero no se bajan.
  const vieja = !!plan.data && plan.data.schemaVersion < INTERVIEW_GUIDE_SCHEMA_VERSION;
  const ingles = plan.data?.jdSnapshot?.englishRequired;

  return (
    <Dialog open={!!planId} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-4 w-4 text-brand-green" />
            {plan.data?.position ?? ''}
          </DialogTitle>
        </DialogHeader>
        {plan.isLoading && <div className="flex justify-center py-16"><WinclapLoader size={32} /></div>}
        {plan.error && (
          <Alert variant="destructive">
            <AlertTitle>{t(`${k}.load_error`)}</AlertTitle>
            <AlertDescription>{plan.error.message}</AlertDescription>
          </Alert>
        )}
        {vieja && (
          <Alert>
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>{t(`${k}.legacy_title`)}</AlertTitle>
            <AlertDescription>{t(`${k}.legacy_desc`)}</AlertDescription>
          </Alert>
        )}
        {plan.data && g && !vieja && (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                {g.area} · {g.impact_level} · {g.location}
                {ingles !== undefined && (
                  <Badge variant="outline" className="gap-1 font-normal">
                    <Languages className="h-3 w-3" />
                    {t(ingles ? `${k}.english_required` : `${k}.english_not_required`)}
                  </Badge>
                )}
              </span>
              <Button size="sm" className="gap-1.5" disabled={exportar.isPending} onClick={() => exportar.mutate({ planId: planId! })}>
                {exportar.isPending ? <WinclapLoader size={14} /> : <FileDown className="h-3.5 w-3.5" />}
                {t(`${k}.download`)}
              </Button>
            </div>

            <Entrevistadores plan={plan.data} canManage={canManage} />

            {!!plan.data?.warnings.length && (
              <Alert>
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>{t(`${k}.warnings_title`)}</AlertTitle>
                <AlertDescription>
                  <ul className="mt-1 list-disc space-y-0.5 pl-4 text-xs">
                    {plan.data.warnings.map((w, i) => <li key={i}>{w.detail}</li>)}
                  </ul>
                </AlertDescription>
              </Alert>
            )}

            {([
              ['ta', g.ta.questions.length + (g.ta.english_block.required ? g.ta.english_block.questions?.length ?? 0 : 0), g.ta.objective],
              ['hm', g.hm.questions.length, g.hm.objective],
              ['br', g.br.questions.length, g.br.objective],
            ] as const).map(([stage, n, objetivo]) => (
              <section key={stage} className="rounded-lg border p-3">
                <div className="mb-1 flex items-center justify-between gap-2">
                  <h4 className="text-sm font-bold">{t(`${k}.stage_${stage}`)}</h4>
                  <Badge variant="secondary">{t(`${k}.n_questions`, { count: n })}</Badge>
                </div>
                <p className="text-sm text-muted-foreground">{objetivo}</p>
              </section>
            ))}

            {plan.data.jdSnapshot && (
              <details className="rounded-lg border bg-muted/30 p-2">
                <summary className="cursor-pointer text-xs font-medium">
                  {t(`${k}.jd_snapshot`, { at: formatDateTime(plan.data.jdSnapshot.takenAt) })}
                </summary>
                <pre className="mt-2 max-h-64 overflow-y-auto whitespace-pre-wrap font-sans text-xs">{plan.data.jdSnapshot.texto}</pre>
              </details>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function TaInterviewTab({ canManage, canAdmin }: { canManage: boolean; canAdmin: boolean }) {
  const { t } = useTranslation();
  const utils = trpc.useUtils();
  const [abrirGenerar, setAbrirGenerar] = useState(false);
  const [verPlan, setVerPlan] = useState<string | null>(null);
  const [siguiendo, setSiguiendo] = useState<string | null>(null);
  // Mientras haya una guía generándose, la lista se refresca sola cada 10 s:
  // el trabajo corre en segundo plano y tarda de 2 a 4 minutos.
  const lista = trpc.taMetrics.interview.list.useQuery(undefined, {
    staleTime: 10_000,
    refetchInterval: (q) => (q.state.data?.some((p) => p.status === 'GENERATING') ? 10_000 : false),
  });

  // Avisar cuando la que se pidió recién termina, sin importar si se cerró el diálogo.
  const seguida = siguiendo ? (lista.data ?? []).find((p) => p.planId === siguiendo) : undefined;
  useEffect(() => {
    if (!seguida || seguida.status === 'GENERATING') return;
    if (seguida.status === 'READY') {
      toast.success(t(`${k}.generated`, { position: seguida.position, minutes: Math.max(1, Math.round(seguida.ms / 60000)) }));
      if (seguida.warnings.length) toast.warning(t(`${k}.with_warnings`, { count: seguida.warnings.length }));
    } else {
      toast.error(seguida.error ?? t(`${k}.generate_failed`));
    }
    setSiguiendo(null);
  }, [seguida, t]);
  const borrar = trpc.taMetrics.interview.remove.useMutation({
    onSuccess: () => { toast.success(t(`${k}.removed`)); utils.taMetrics.interview.list.invalidate(); },
    onError: (e) => toastError(e, t(`${k}.remove_failed`)),
  });

  const { pageItems, footer } = usePagination((lista.data ?? []) as Plan[], 25);
  const personas = usePersonas(canManage);

  return (
    <div className="space-y-4">
      {/* El texto que explica qué hace el agente vive en el encabezado de la
          página (TaInterviewPage): acá sería el mismo párrafo dos veces. */}
      {canManage && (
        <div className="flex flex-wrap items-center justify-end gap-3">
          <Button size="sm" className="gap-1.5" onClick={() => setAbrirGenerar(true)}>
            <Sparkles className="h-3.5 w-3.5" />
            {t(`${k}.new`)}
          </Button>
        </div>
      )}

      {lista.isLoading && <div className="flex justify-center py-24"><WinclapLoader size={40} /></div>}
      {lista.error && (
        <Alert variant="destructive">
          <AlertTitle>{t(`${k}.load_error`)}</AlertTitle>
          <AlertDescription>{lista.error.message}</AlertDescription>
        </Alert>
      )}

      {lista.data && (
        <>
          <div className="rounded-lg border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t(`${k}.col_position`)}</TableHead>
                  <TableHead>{t(`${k}.col_interviewers`)}</TableHead>
                  <TableHead>{t(`${k}.col_source`)}</TableHead>
                  <TableHead>{t(`${k}.col_created`)}</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {pageItems.map((p) => (
                  <TableRow
                    key={p.planId}
                    className={p.status === 'READY' ? 'cursor-pointer' : undefined}
                    onClick={() => { if (p.status === 'READY') setVerPlan(p.planId); }}
                  >
                    <TableCell className="font-medium">
                      {p.status === 'GENERATING'
                        ? <span className="inline-flex items-center gap-2 text-muted-foreground"><WinclapLoader size={14} />{t(`${k}.in_progress`)}</span>
                        : p.position || '—'}
                      {p.status === 'FAILED' && (
                        <Badge variant="destructive" className="ml-2">{t(`${k}.failed`)}</Badge>
                      )}
                      {!!p.warnings.length && (
                        <Badge variant="outline" className="ml-2 border-amber-400/50 text-amber-600">
                          {t(`${k}.n_warnings`, { count: p.warnings.length })}
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      <div>HM: {personas.nombre(p.hmEmail) ?? '—'}</div>
                      <div>BR: {personas.nombre(p.brEmail) ?? '—'}</div>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">{t(`${k}.source_${p.jd.source}`)}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {formatDateTime(p.createdAt)} · {p.createdBy}
                    </TableCell>
                    <TableCell className="text-right">
                      {canAdmin && (
                        <Button
                          size="sm"
                          variant="ghost"
                          disabled={borrar.isPending}
                          onClick={(e) => { e.stopPropagation(); borrar.mutate({ planId: p.planId }); }}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {pageItems.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} className="py-12 text-center text-sm text-muted-foreground">
                      {t(canManage ? `${k}.empty` : `${k}.empty_interviewer`)}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
          {footer}
        </>
      )}

      {canManage && <GenerarDialog
        open={abrirGenerar}
        onClose={() => setAbrirGenerar(false)}
        onDone={() => utils.taMetrics.interview.list.invalidate()}
        onSeguir={setSiguiendo}
      />}
      <GuiaDialog planId={verPlan} onClose={() => setVerPlan(null)} canManage={canManage} />
    </div>
  );
}
